import { Component, computed, inject } from '@angular/core';
import { ModuleService } from 'src/app/shared/services/module.service';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { ModuleItem, ModuleMatrixRow } from '../module-model';
import { AppState } from 'src/app/app.state';
import { OrganizationType } from 'src/app/shared/enums/organization-type.enum';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { AsyncPipe, CommonModule, KeyValuePipe } from '@angular/common';
import { TranslateOrganizationTypeEnum } from '../../../shared/pipes/translate-organization-type-enum.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RequiresFeatureRoleDirective } from 'src/app/shared/directives/requires-feature-role.directive';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { ModuleListStateService } from './module-list.state.service';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { SnackbarService } from 'ok-angular-shared/components';
import { Permission } from 'src/app/shared/enums/permission.enum';

@Component({
  selector: 'adm-module-list',
  templateUrl: './module-list.component.html',
  styleUrls: ['./module-list.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    LoadingSpinnerComponent,
    KeyValuePipe,
    TranslateOrganizationTypeEnum,
    RequiresFeatureRoleDirective,
    TextSearchComponent,
    AsyncPipe,
    MatTooltip,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatPaginator,
    MatSortModule,
    MatProgressSpinner,
    MatSlideToggle,
  ],
  providers: [ModuleListStateService, SnackbarService],
})
export class ModuleListComponent {
  private moduleService = inject(ModuleService);
  appState = inject(AppState);
  state = inject(ModuleListStateService);
  private snackbarService = inject(SnackbarService);
  authService = inject(AuthService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  public roles = this.environmentLoaderService.virtualRoles;
  public organizationType: typeof OrganizationType = OrganizationType;
  public Permission = Permission;
  public data = toSignal(this.state.data$, { initialValue: null });

  moduleMatrixRows = computed(() => this.data()?.moduleMatrixRows ?? null);

  public sortData(event: Sort) {
    this.state.filters.update(x => ({
      ...x,
      sort: event,
      page: { ...x.page, pageIndex: 0 },
    }));
  }

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(x => ({
      ...x,
      page: event,
    }));
  }

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  public async updateMatrix(matrixRow: ModuleMatrixRow, moduleItem: ModuleItem) {
    if (moduleItem.organizationHasModule) {
      try {
        await firstValueFrom(
          this.moduleService.createModuleMatrix(matrixRow.organizationId, moduleItem.module.id).pipe(
            tap(() => {
              this.state.refresh();
            })
          )
        );

        this.snackbarService.success('Aktivering gennemført');
      } catch (error) {
        const typedError = error as { error?: { code?: number } };
        if (typedError.error?.code === 100903) {
          this.snackbarService.warning('Ønsket ændring findes allerede');
        } else {
          this.snackbarService.warning('Der er sket en uhåndteret fejl. Dit valg blev ikke gemt.');
        }
      }
    } else {
      firstValueFrom(
        this.moduleService.deleteModuleMatrix(matrixRow.organizationId, moduleItem.module.id).pipe(
          tap(() => {
            this.state.refresh();
          })
        )
      );
      this.snackbarService.success('Deaktivering gennemført');
    }
  }

  public async removeModuleFromOrganizations(moduleId: number) {
    this.setUpdating(moduleId, true);
    await firstValueFrom(
      this.moduleService.deleteModuleMatrices(moduleId).pipe(
        tap(() => {
          this.setUpdating(moduleId, false);
          this.state.refresh();
        })
      )
    );
  }

  public async addModuleToOrganizations(moduleId: number, organizationType?: string) {
    this.setUpdating(moduleId, true);
    await firstValueFrom(
      this.moduleService.createModuleMatrices(moduleId, organizationType ? (organizationType as OrganizationType) : null).pipe(
        tap(() => {
          this.setUpdating(moduleId, false);
          this.state.refresh();
        })
      )
    );
  }

  private setUpdating(moduleId: number, isUpdating: boolean) {
    this.moduleMatrixRows()?.forEach(moduleMatrixRow => {
      moduleMatrixRow.modules
        .filter(m => m.module.id === moduleId)
        .forEach(m => {
          m.updating.next(isUpdating);
        });
    });
  }
}
